import React, { useEffect, useState } from 'react';
import { Check, Loader2 } from 'lucide-react';

/**
 * Tiny "Last saved 12 sec ago" pill that lives next to the Save Draft
 * button. Self-updates every 5 seconds purely on the client — no
 * polling, no API calls, no DB hits. The parent already tracks
 * `lastSaved` state which is set on every successful save (manual or
 * autosave); we just translate that timestamp into a friendly relative
 * label so field workers can tell at a glance whether their work made
 * it to the server.
 */
export default function LastSavedIndicator({ lastSaved, saving }) {
  const [, setTick] = useState(0);

  // Re-render every 5 seconds so the relative label stays fresh.
  // Skipped entirely until we have a `lastSaved` to display.
  useEffect(() => {
    if (!lastSaved) return undefined;
    const id = setInterval(() => setTick((t) => t + 1), 5000);
    return () => clearInterval(id);
  }, [lastSaved]);

  if (saving) {
    return (
      <span
        className="inline-flex items-center gap-1 text-xs text-slate-500"
        data-testid="last-saved-indicator-saving"
      >
        <Loader2 className="w-3 h-3 animate-spin" />
        Saving…
      </span>
    );
  }

  if (!lastSaved) {
    return (
      <span
        className="inline-flex items-center gap-1 text-xs text-slate-400"
        data-testid="last-saved-indicator-never"
      >
        Not yet saved
      </span>
    );
  }

  const secs = Math.max(0, Math.floor((Date.now() - new Date(lastSaved).getTime()) / 1000));
  let label;
  if (secs < 5) label = 'Saved just now';
  else if (secs < 60) label = `Saved ${secs} sec ago`;
  else if (secs < 3600) label = `Saved ${Math.floor(secs / 60)} min ago`;
  else label = `Saved ${Math.floor(secs / 3600)} hr ago`;

  // Color: green when fresh (< 30s), amber when stale (> 2 min), slate otherwise.
  const tone =
    secs < 30 ? 'text-emerald-600' : secs > 120 ? 'text-amber-600' : 'text-slate-500';

  return (
    <span
      className={`inline-flex items-center gap-1 text-xs ${tone}`}
      data-testid="last-saved-indicator"
      title={new Date(lastSaved).toLocaleString()}
    >
      <Check className="w-3 h-3" />
      {label}
    </span>
  );
}
